import {z} from "zod/v4-mini"
import {graphNode} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BinaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import {binaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef, ValueRef} from "#template/geometry-processing/geometry-graph"
import {AttributeRef as V1AttributeRef} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type BinaryMathOperatorParameters<TAttribute> = {
    arg1: TAttribute | null
    arg2: TAttribute | GeometryValueNode | NumberLike | null
    operator: BinaryMathOperatorOption
}

export const binaryMathOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        arg1: z.nullable(attributeSchema),
        arg2: z.nullable(z.union([attributeSchema, graphNode(GeometryValueNode), numberLike])),
        operator: binaryMathOperatorOption,
    })

function applyBinaryMathOperator<T extends IndexingDim>(
    arg1: GeometryAttributeData<T>,
    arg2: GeometryAttributeData<T> | GeometryValueData | number,
    operator: BinaryMathOperatorOption,
): GeometryAttributeData<T> {
    const arg1AttrRefData = arg1.ref
    const arg2AttrRefData = resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber(arg2)

    const binaryMathAttrRefData = applyGeometryOperator(
        (arg1: AttributeRef, arg2: AttributeRef | ValueRef | number) => {
            switch (operator) {
                case "add":
                    return arg1.add(arg2)
                case "sub":
                    return arg1.sub(arg2)
                case "mul":
                    return arg1.mul(arg2)
                case "div":
                    return arg1.div(arg2)
                case "mod":
                    return arg1.mod(arg2)
                case "gt":
                    return arg1.gt(arg2)
                case "lt":
                    return arg1.lt(arg2)
                case "eq":
                    return arg1.eq(arg2)
                case "ne":
                    return arg1.ne(arg2)
                case "lte":
                    return arg1.lte(arg2)
                case "and":
                    return arg1.and(arg2)
                case "or":
                    return arg1.or(arg2)
                case "gte":
                    return arg1.gte(arg2)
                case "cross":
                    if (!(arg2 instanceof V1AttributeRef)) throw new Error("Cross product requires two vector attributes")
                    if (arg1._type !== "float3" || arg2._type !== "float3") throw new Error("Cross product requires two float3 attributes")
                    return arg1.cross(arg2)
                default:
                    throw new Error(`Unknown binary math operator: ${operator}`)
            }
        },
        arg1AttrRefData,
        arg2AttrRefData,
    )

    return createGeometryAttributeData(binaryMathAttrRefData, arg1.dim)
}

export abstract class GeometryBinaryMathOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, BinaryMathOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: BinaryMathOperatorParameters<TAttribute>,
    ): parameters is BinaryMathOperatorParameters<TAttribute> & NonNullableParameters<BinaryMathOperatorParameters<TAttribute>> {
        return parameters.arg1 !== null && parameters.arg2 !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<BinaryMathOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {arg1, arg2, operator: operatorName} = parameters

        const [evaluatedArg1Valid, evaluatedArg1Invalid] = scope.branch(evaluator.evaluate(arg1))
        const [evaluatedArg2Valid, evaluatedArg2Invalid] = scope.branch(evaluator.evaluate(arg2))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedArg1Valid, evaluatedArg2Valid, operatorName),
            ([arg1, arg2, operatorName]) => applyBinaryMathOperator(arg1, arg2, operatorName),
            "applyBinaryMathOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(evaluatedArg1Invalid, evaluatedArg2Invalid)}
    }
}
